import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, Stack, Typography} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import { usePost } from '../../../apis/ApiRequests';
import Header from '../../../components/uiParts/hygiene/Header';
import TitleBar from '../../../components/uiParts/TitleBar';
import MenuIcon from '../../../components/uiParts/MenuIcon';
import PrintIcon from '@mui/icons-material/Print';
import DateOutlinedInput from '../../../components/input/DateOutlinedInput';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CloseIcon from '@mui/icons-material/Close';
import { generateDailyReportPDF,generateMonthlyReportPDF } from '../exportReportPdf';
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { format } from 'date-fns';
// ---------------------------
// date utils
// ---------------------------
function addMonths(d, diff) {
	return new Date(d.getFullYear(), d.getMonth() + diff, 1);
}

const HygieneReportMenu = () => {
    
    const { authInfo, setAuthInfo } = useAuth();
    const navigate = useNavigate();

    //===============================================
    // initialize
    //===============================================
    //-----------------------------------------------
    // initialize - セッション情報を取得
    //-----------------------------------------------
    const facilityCode = authInfo.facilityCode;
    const facilityName = authInfo.facilityName;
    const userId       = authInfo.userId;
    const userName     = authInfo.userName;
    const authority    = authInfo.authority;
    const traderCode   = authInfo.traderCode;
    const trader       = authInfo.trader;
    const today        = authInfo.targetDate;

    //-----------------------------------------------
    // initialize - useState
	//-----------------------------------------------
    const [ openDialogDailyPrint,   setOpenDialogDailyPrint    ] = useState(false);
    const [ openDialogMonthlyPrint, setOpenDialogMonthlyPrint  ] = useState(false);
    const [ targetDate,             setTargetDate              ] = useState(today);
    const [ haccpMasta,             setHaccpMasta              ] = useState([]);
    const [ userList,               setUserList              ] = useState([]);

    // 表示中の月
    const [viewMonth, setViewMonth] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });
    const headerLabel = useMemo(() => {
        const y = viewMonth.getFullYear();
        const m = viewMonth.getMonth() + 1;
        return `${y}年 ${m}月`;
    }, [viewMonth]);

    //===============================================
    // API
    //===============================================
    //-----------------------------------------------
    // API - searchData / Haccpマスター情報を取得する
    //-----------------------------------------------
    const { response: searchData_Res, post: searchData_Post } = usePost();
    const searchData = async () => {
        const postData = {
            'className'   : 'mch.models.masta.Haccp',
            'methodName'  : 'searchData',
            'facilityCode': facilityCode,
            'userId'      : userId,
        }
        await searchData_Post(postData);
    };
    //-----------------------------------------------
    // API - searchData / masta_User
    //-----------------------------------------------
    const { response : searchUserData_res, post:searchUserData_post } = usePost();
    const searchUserData = async () => {
        const postData = {
            'className'    : 'mch.models.masta.User',
            'methodName'   : 'searchData',
            'facilityCode' : facilityCode,
            'keyword1'     : "",
        }
        await searchUserData_post(postData);
    };
    //-----------------------------------------------
    // API - 該当日のチェックデータを取得する
    //-----------------------------------------------
    const { response: getDairyData_res, post: getDairyData_post } = usePost();
    const getDairyData = async () => {
        const formattedTargetDate = targetDate?.replaceAll("-","");
        const postData = {
            'className'    : 'mch.models.table.HygieneList',
            'methodName'   : 'searchData',
            'facilityCode' : facilityCode,
            'startDate'    : formattedTargetDate,
            'endDate'      : formattedTargetDate,
        }
        await getDairyData_post(postData);
    };
    //-----------------------------------------------
    // API - 該当日の情報を取得する
    //-----------------------------------------------
    const { response: getReportData_res, post: getReportData_post } = usePost();
    const getReportData = async () => {
        const postData = {
            'className'    : 'mch.models.table.HygieneHistory',
            'methodName'   : 'searchDataByTargetDate',
            'facilityCode' : facilityCode,
            'entryDate'    : targetDate?.replaceAll("-",""),
        }
        await getReportData_post(postData);
    }
    //-----------------------------------------------
    // API - 該当月のチェックデータを取得する
    //-----------------------------------------------
    const { response: getMonthlyItemReport_res, post: getMonthlyItemReport_post } = usePost();
    const getMonthlyItemReport = async (targetDatas) => {
        const postData = {
            'className'    : 'mch.models.table.HygieneHistory',
            'methodName'   : 'searchDataListByRecent',
            'facilityCode' : facilityCode,
            'entryDate'    : targetDatas.Data?.map(data => data.entryDate),
        }
        await getMonthlyItemReport_post(postData);
    }
    //-----------------------------------------------
    // API - 該当月のHygieneIdを取得する
    //-----------------------------------------------
    const { response: getMonthlyReport_res, post: getMonthlyReport_post } = usePost();
    const getMonthlyReport = async () => {
        const postData = {
            'className'   : 'mch.models.table.HygieneList',
            'methodName'  : 'searchData',
            'facilityCode': facilityCode,
            'startDate'   : format(viewMonth,"yyyyMMdd"),
            'endDate'     : format(addMonths(viewMonth,1).setDate(0),"yyyyMMdd"),
        };
        await getMonthlyReport_post(postData);
    };
    //===============================================
    // useEffect
    //===============================================
    //-----------------------------------------------
    // useEffect - ロード時の設定
    //-----------------------------------------------
    useEffect(() => {
        const fetchData = async () => {
            await searchData();
            await searchUserData();
        };
        fetchData();
    }, [])
    //-----------------------------------------------
    // useEffect - 日報印刷
    //-----------------------------------------------
    useEffect(()=>{
        if(getDairyData_res){
            getReportData();
        }
    }, [getDairyData_res])

    useEffect(() => {
        if(getReportData_res && getDairyData_res){
            generateDailyReportPDF({
                "entryDate": targetDate,
                userName,
                haccpMasta,
                "dairyData":getDairyData_res,
                "checkData":getReportData_res,
                userList
            });
        }
    }, [getReportData_res])
    //-----------------------------------------------
    // useEffect - searchData / haccp
    //-----------------------------------------------
    useEffect(() => {
        setHaccpMasta([]);
        if (searchData_Res) {
            setHaccpMasta(searchData_Res.Data);
        }
    }, [searchData_Res]);
    //-----------------------------------------------
    // useEffect - searchData / user
    //-----------------------------------------------
    useEffect(() => {
        setUserList([]);
        if (searchUserData_res) {
            setUserList(searchUserData_res.Data);
        }
    }, [searchUserData_res]);
    //-----------------------------------------------
    // useEffect -
    //-----------------------------------------------
    useEffect(() => {
        if (getMonthlyReport_res) {
            getMonthlyItemReport(getMonthlyReport_res)
        }
    }, [getMonthlyReport_res]);

    //-----------------------------------------------
    // useEffect - 
    //-----------------------------------------------
    useEffect(() => {
        if (getMonthlyItemReport_res ) {
            generateMonthlyReportPDF(
                {
                    userName,
                    "monthlyData":getMonthlyReport_res,
                    "monthlyItemCheckData":getMonthlyItemReport_res,
                    haccpMasta,
                    "targetMonth": format(viewMonth,"yyyy-MM")
                }
            );
        }
    }, [getMonthlyItemReport_res]);
    //===============================================
    // eventHandler
    //===============================================
    const handleClick = (url) => {
        navigate(url);
    };
    //-----------------------------------------------
    // eventHandler - 日付の変更
    //-----------------------------------------------
    const handleChange_entryDate = (value) => {
    };
    //-----------------------------------------------
    // eventHandler - 前月へ移動
    //-----------------------------------------------
	function handlePrev() {
		setViewMonth((d) => addMonths(d, -1));
	}
    //-----------------------------------------------
    // eventHandler - 翌月へ移動
    //-----------------------------------------------
	function handleNext() {
		setViewMonth((d) => addMonths(d, 1));
	}
	//-----------------------------------------------
    // eventHandler - 日報印刷の実行
	//-----------------------------------------------
    const handleClick_dailyPrint_execute = async() => {
        await getDairyData();
        
    };
	//-----------------------------------------------
    // eventHandler - 日報印刷ダイアログを非表示
	//-----------------------------------------------
    const handleClick_dailyPrint_close = () => {
        setOpenDialogDailyPrint(false);
    };
	//-----------------------------------------------
    // eventHandler - 月報印刷の実行
	//-----------------------------------------------
    const handleClick_MonthlyPrint_execute = () => {
        getMonthlyReport();
    };
	//-----------------------------------------------
    // eventHandler - 月報印刷ダイアログを非表示
	//-----------------------------------------------
    const handleClick_MonthlyPrint_close = () => {
        setOpenDialogMonthlyPrint(false);
    };
    //===============================================
    // Function
    //===============================================
    //-----------------------------------------------
    // Function - hygieneId を並び替え
    //-----------------------------------------------
    const sortHygieneById = (dailyReport = []) => {
        return [...dailyReport].sort((a, b) => a.hygieneId - b.hygieneId);
    };
    //===============================================
    // Css
    //===============================================
    const dialog_button_close = { 
        fontSize: '16px', height:'40px', width:'460px', color:'#fff', backgroundColor:'#808080'
    };
    //===============================================
    // JSXのレンダー
    //===============================================
    return (
        <>
            <Header loginInfo="on" backButton="on" backPage="/hygieneHome" setEntryDate={handleChange_entryDate} />
            <div className='hygieneReportMenu' style={{paddingTop:'2px', paddingLeft:'10px', paddingRight:'10px', paddingBottom:'10px'}}>
                <div>
                    <TitleBar title="帳票出力" color="#d66a35" />
                    <MenuIcon header="report Menu" title="日報印刷"  onClick={() => setOpenDialogDailyPrint(true)} color="#d66a35">
                        <PrintIcon style={{fontSize: '4rem', color: '#d66a35'}} />
                    </MenuIcon>
                    <MenuIcon header="report Menu" title="月報印刷"  onClick={() => setOpenDialogMonthlyPrint(true)} color="#d66a35">
                        <PrintIcon style={{fontSize: '4rem', color: '#d66a35'}} />
                    </MenuIcon>
                </div>
            </div>
            <div className='dailyPrint_dialog'>
                <Dialog open={openDialogDailyPrint} onClose={handleClick_dailyPrint_close} maxWidth='lg' >
                    <DialogTitle>
                        <TitleBar title="日報の印刷" />
                    </DialogTitle>
                    <DialogContent style={{ border: 'none' }} sx={{ width: 'auto' }}>
                        <div style={{ backgroundColor: '#efefef', padding: '10px' }}>
                            <div style={{ display: 'flex', marginRight: '10px', marginTop: '15px' }}>
                                <div>
                                    <DateOutlinedInput
                                        id="csvExport_dialog"
                                        label="対象日"
                                        value={targetDate}
                                        onChange={(e) => setTargetDate(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div style={{ marginTop: '10px' }}>
                                <Button
                                    color="secondary"
                                    size="large"
                                    variant="contained"
                                    sx={{ marginLeft: '0px', color: '#fff', width: '440px' }}
                                    onClick={() => handleClick_dailyPrint_execute()}
                                    startIcon={<PrintIcon />}
                                >{'印刷'}
                                </Button>
                            </div>
                        </div>
                        <div style={{ marginTop: '40px', marginLeft: '0px' }}>
                            <Button
                                color="tertiary"
                                size="large"
                                variant="contained"
                                sx={dialog_button_close}
                                onClick={() => handleClick_dailyPrint_close()}
                                startIcon={<CloseIcon />}
                            >{'閉じる'}
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
            <div className='dailyPrint_dialog'>
                <Dialog open={openDialogMonthlyPrint} onClose={handleClick_MonthlyPrint_close} maxWidth='lg' >
                    <DialogTitle>
                        <TitleBar title="月報の印刷" />
                    </DialogTitle>
                    <DialogContent style={{ border: 'none' }} sx={{ width: 'auto' }}>
                        <div style={{ backgroundColor: '#efefef', padding: '10px' }}>
                            <div style={{ display: 'flex', marginRight: '10px', marginTop: '15px' }}>
                                <div>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <IconButton onClick={handlePrev} aria-label="prev month">
                                                <ChevronLeftIcon />
                                            </IconButton>

                                            <Typography variant="h7" sx={{ ml: 1, fontWeight: 'bold' }}>
                                                {headerLabel}
                                            </Typography>


                                            <IconButton onClick={handleNext} aria-label="next month">
                                                <ChevronRightIcon />
                                            </IconButton>
                                        </Stack>

                                        <Box />
                                    </Stack>
                                </div>
                            </div>
                            <div style={{ marginTop: '10px' }}>
                                <Button
                                    color="secondary"
                                    size="large"
                                    variant="contained"
                                    sx={{ marginLeft: '0px', color: '#fff', width: '440px' }}
                                    onClick={() => handleClick_MonthlyPrint_execute()}
                                    startIcon={<PrintIcon />}
                                >{'印刷'}
                                </Button>
                            </div>
                        </div>
                        <div style={{ marginTop: '40px', marginLeft: '0px' }}>
                            <Button
                                color="tertiary"
                                size="large"
                                variant="contained"
                                sx={dialog_button_close}
                                onClick={() => handleClick_MonthlyPrint_close()}
                                startIcon={<CloseIcon />}
                            >{'閉じる'}
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>

        </>
    )
}
export default HygieneReportMenu;